import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Eyebrow } from '@/components/ui/eyebrow';
import { ErrorState } from '@/components/ErrorState';
import { useBottleneck, useFunnel, useSummary, useDashboardConfig, useDiagnostic } from '@/lib/api/hooks/useAnalytics';
import { KpiCard } from '@/components/analytics/KpiCard';
import { DiagnosticSummary } from '@/components/analytics/DiagnosticSummary';
import { TIER2_MAPPINGS, KPI_META } from '@/components/analytics/tier2-mappings';
import type {
  Tier1KpiKey,
  Severity,
  DataQuality,
  BottleneckResponse,
  FunnelResponse,
  FunnelStage,
  SummaryData,
} from '@/types/analytics';

// ---------------------------------------------------------------------------
// Value extraction - maps a KPI key to display data from combined API responses.
// All percent values from the bottleneck API are already 0-100 scale.
// ---------------------------------------------------------------------------

interface KpiDisplayData {
  value: string;
  severity: Severity;
  subtitle: string;
  dataQuality?: DataQuality;
  rawValue?: number;
}

function getKpiData(
  key: Tier1KpiKey,
  bottleneck: BottleneckResponse | undefined,
  summary: SummaryData | null | undefined,
  funnel: FunnelResponse | undefined,
): KpiDisplayData {
  switch (key) {
    case 'show_rate':
      if (!bottleneck) return { value: ' - ', severity: 'green', subtitle: '' };
      return {
        value: `${bottleneck.show_rate.rate.toFixed(1)}%`,
        severity: bottleneck.show_rate.severity,
        subtitle: '',
        dataQuality: bottleneck.show_rate.data_quality,
        rawValue: bottleneck.show_rate.rate,
      };

    case 'close_rate':
      if (!bottleneck) return { value: ' - ', severity: 'green', subtitle: '' };
      return {
        value: `${bottleneck.close_rate.rate.toFixed(1)}%`,
        severity: bottleneck.close_rate.severity,
        subtitle: '',
        dataQuality: bottleneck.close_rate.data_quality,
        rawValue: bottleneck.close_rate.rate,
      };

    case 'cpl':
      if (!summary) return { value: ' - ', severity: 'green', subtitle: 'No campaign data' };
      if (summary.spend < 0.01) {
        return { value: ' - ', severity: 'green', subtitle: 'Connect Meta to populate' };
      }
      if (summary.leads === 0) {
        return { value: ' - ', severity: 'yellow', subtitle: `$${summary.spend.toFixed(0)} spend · 0 leads` };
      }
      return {
        value: `$${summary.cpl.toFixed(2)}`,
        severity: summary.cpl > 80 ? 'red' : summary.cpl > 40 ? 'yellow' : 'green',
        subtitle: `${summary.leads.toLocaleString()} leads · $${summary.spend.toFixed(0)} spend`,
        rawValue: summary.cpl,
      };

    case 'speed_to_lead':
      if (!bottleneck) return { value: ' - ', severity: 'green', subtitle: '' };
      return {
        value: `${bottleneck.time_to_first_call.median_minutes.toFixed(0)} min`,
        severity: bottleneck.time_to_first_call.severity,
        subtitle: `Avg: ${bottleneck.time_to_first_call.avg_minutes.toFixed(0)} min`,
        dataQuality: bottleneck.time_to_first_call.data_quality,
        rawValue: bottleneck.time_to_first_call.median_minutes,
      };

    case 'stale_leads':
      if (!bottleneck) return { value: ' - ', severity: 'green', subtitle: '' };
      return {
        value: bottleneck.stale_leads.count.toLocaleString(),
        severity: bottleneck.stale_leads.severity,
        subtitle: 'No activity in 7+ days',
        dataQuality: bottleneck.stale_leads.data_quality,
        rawValue: bottleneck.stale_leads.count,
      };

    case 'qualified_calls':
      if (!bottleneck) return { value: ' - ', severity: 'green', subtitle: '' };
      return {
        value: `${bottleneck.call_quality.qualification_rate.toFixed(1)}%`,
        severity: bottleneck.call_quality.severity,
        subtitle: `${bottleneck.call_quality.qualified_held_calls}/${bottleneck.call_quality.total_held_calls} calls`,
        dataQuality: bottleneck.call_quality.data_quality,
        rawValue: bottleneck.call_quality.qualification_rate,
      };

    case 'revenue':
      if (!funnel) return { value: ' - ', severity: 'green', subtitle: '' };
      return {
        value: funnel.summary.total_revenue >= 1000
          ? `$${(funnel.summary.total_revenue / 1000).toFixed(1)}k`
          : `$${funnel.summary.total_revenue.toFixed(0)}`,
        severity: 'green',
        subtitle: `${funnel.summary.total_leads.toLocaleString()} total leads`,
        rawValue: funnel.summary.total_revenue,
      };

    default:
      return { value: ' - ', severity: 'green', subtitle: '' };
  }
}

// Build a flat map keyed exactly by TIER2_MAPPINGS factor keys.
// Values are pre-resolved here so Tier2Panel does a simple data[key] lookup.
function buildTier2DataMap(
  bottleneck: BottleneckResponse | undefined,
  summary: SummaryData | null | undefined,
  funnel: FunnelResponse | undefined,
): Record<string, unknown> {
  const b = bottleneck;
  const s = summary;
  return {
    'call_quality.qualification_rate':        b?.call_quality?.qualification_rate,
    'stage_dropoffs.showed':                  b?.stage_dropoffs?.showed?.drop_off_pct,
    'call_quality.avg_duration_all':          b?.call_quality?.avg_duration_all,
    'deal_velocity.avg_days_lead_to_close':   b?.deal_velocity?.avg_days_lead_to_close,
    'campaigns_count':                        undefined,  // not in summary; campaigns view has this
    'best_cpl':                               s?.cpl,
    'time_to_first_call.median_minutes':      b?.time_to_first_call?.median_minutes,
    'stale_by_stage':                         undefined,  // breakdown - shows "See detail"
    'call_quality.qualified_held_calls':      b?.call_quality?.qualified_held_calls,
    'call_quality.total_held_calls':          b?.call_quality?.total_held_calls,
    'funnel_close_rate':                      funnel?.summary?.close_rate,
  };
}

// Compute the previous period params: same duration, shifted back by one period.
function computePrevParams(params: { date_from?: string; date_to?: string }): { date_from: string; date_to: string } {
  const to = params.date_to ? new Date(params.date_to) : new Date();
  const from = params.date_from
    ? new Date(params.date_from)
    : new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  const diffMs = to.getTime() - from.getTime();
  const prevTo = new Date(from.getTime() - 24 * 60 * 60 * 1000);
  const prevFrom = new Date(prevTo.getTime() - diffMs);
  return {
    date_from: prevFrom.toISOString().split('T')[0],
    date_to:   prevTo.toISOString().split('T')[0],
  };
}

// ---------------------------------------------------------------------------
// Mini funnel chart - pipeline snapshot for dashboard below KPI row
// ---------------------------------------------------------------------------

const MINI_STAGE_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#ec4899', '#10b981'];

function MiniFunnelChart({ stages, totalLeads }: {
  stages: FunnelStage[];
  totalLeads: number;
}) {
  return (
    <div className="rounded-lg border border-border p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-4">Pipeline Funnel</p>
      <div className="space-y-3">
        {stages.map((stage, i) => {
          const pct = totalLeads > 0 ? (stage.count / totalLeads) * 100 : 0;
          const dropOffText = stage.drop_off_pct != null && stage.drop_off_pct > 0
            ? `${stage.drop_off_pct.toFixed(0)}% drop-off`
            : null;
          return (
            <div key={stage.name}>
              <div className="flex items-center gap-3">
                <span className="w-28 text-xs text-right text-muted-foreground shrink-0 capitalize">
                  {stage.name.replace(/_/g, ' ')}
                </span>
                <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.max(pct, stage.count > 0 ? 1 : 0)}%`,
                      backgroundColor: MINI_STAGE_COLORS[i % MINI_STAGE_COLORS.length],
                    }}
                  />
                </div>
                <span className="w-10 text-xs font-semibold tabular-nums text-right">
                  {stage.count.toLocaleString()}
                </span>
                <span className="w-16 text-[10px] text-muted-foreground/60 text-right">
                  {pct > 0 ? `${pct.toFixed(0)}%` : ''}
                </span>
              </div>
              {dropOffText && (
                <div className="flex justify-end mr-16">
                  <span className="text-[10px] text-amber-500/70">↓ {dropOffText}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-4 text-right">
        {totalLeads.toLocaleString()} total leads
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pipeline stats row - 4 key metrics for the dashboard overview
// ---------------------------------------------------------------------------

function PipelineStats({ funnel, bottleneck }: {
  funnel: FunnelResponse;
  bottleneck: BottleneckResponse | undefined;
}) {
  const fmtRevenue = (n: number) =>
    n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(0)}`;

  const stats: Array<{ label: string; value: string; sub: string }> = [
    {
      label: 'Total Leads',
      value: funnel.summary.total_leads.toLocaleString(),
      sub: 'in pipeline',
    },
    {
      label: 'Close Rate',
      value: `${funnel.summary.close_rate.toFixed(1)}%`,
      sub: 'lead → closed won',
    },
    {
      label: 'Pipeline Revenue',
      value: fmtRevenue(funnel.summary.total_revenue),
      sub: 'attributed to pipeline',
    },
    {
      label: 'Show Rate',
      value: bottleneck ? `${bottleneck.show_rate.rate.toFixed(1)}%` : ' - ',
      sub: 'booked → showed',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {stats.map(stat => (
        <div key={stat.label} className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{stat.label}</p>
          <p className="text-2xl font-bold mt-1 leading-tight">{stat.value}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{stat.sub}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function KpiSkeleton() {
  return (
    <div className="rounded-lg border border-border p-4 space-y-2">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="h-9 w-24" />
      <Skeleton className="h-3 w-28" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

const DEFAULT_KPIS: string[] = ['show_rate', 'close_rate', 'cpl', 'speed_to_lead', 'stale_leads'];

export function AnalyticsDashboard() {
  const [searchParams] = useSearchParams();
  const params = {
    date_from: searchParams.get('from') ?? undefined,
    date_to:   searchParams.get('to') ?? undefined,
  };
  const prevParams = computePrevParams(params);

  const [openKey, setOpenKey] = useState<string | null>(null);

  const { data: bottleneckData, isLoading: loadingBottleneck, isError: errBottleneck, refetch: refetchBottleneck } = useBottleneck(params);
  const { data: summaryData,    isLoading: loadingSummary,    isError: errSummary,    refetch: refetchSummary    } = useSummary(params);
  const { data: funnelData,     isLoading: loadingFunnel,     isError: errFunnel,     refetch: refetchFunnel     } = useFunnel(params);
  const { data: configData,     isLoading: loadingConfig     } = useDashboardConfig();
  const { data: diagnosticData, isLoading: loadingDiagnostic } = useDiagnostic(params);

  // Previous period (parallel fetches - TanStack Query deduplicates by key)
  const { data: prevBottleneckData } = useBottleneck(prevParams);
  const { data: prevSummaryData    } = useSummary(prevParams);
  const { data: prevFunnelData     } = useFunnel(prevParams);

  const isLoading = loadingBottleneck || loadingSummary || loadingFunnel || loadingConfig;
  // R3b: AnalyticsDashboard previously had NO error state. Surface a consistent
  // one when every primary query failed (reads the existing react-query isError).
  const allErrored = errBottleneck && errSummary && errFunnel;
  const kpis = configData?.tier1_kpis ?? DEFAULT_KPIS;
  const summaryInner     = summaryData?.summary     ?? null;
  const prevSummaryInner = prevSummaryData?.summary ?? null;
  const tier2DataMap = buildTier2DataMap(bottleneckData, summaryInner, funnelData);

  const kpiGrid = isLoading ? (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
      {Array.from({ length: DEFAULT_KPIS.length }).map((_, i) => (
        <KpiSkeleton key={i} />
      ))}
    </div>
  ) : (!bottleneckData && !summaryInner && !funnelData) ? (
    <div className="py-16 text-center text-sm text-muted-foreground">
      No analytics data for this period
    </div>
  ) : (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
      {kpis.map((kpiKey) => {
        const meta    = KPI_META[kpiKey] ?? { label: kpiKey, target: '', higherIsBetter: true };
        const display = getKpiData(kpiKey as Tier1KpiKey, bottleneckData, summaryInner, funnelData);
        const prev    = getKpiData(kpiKey as Tier1KpiKey, prevBottleneckData, prevSummaryInner, prevFunnelData);
        const tier2   = TIER2_MAPPINGS[kpiKey] ?? [];

        return (
          <KpiCard
            key={kpiKey}
            label={meta.label}
            value={display.value}
            severity={display.severity}
            target={meta.target || undefined}
            subtitle={display.subtitle || undefined}
            dataQuality={display.dataQuality}
            showDataQualityBadge={kpiKey === 'cpl'}
            tier2Factors={tier2}
            tier2Data={tier2DataMap}
            isOpen={openKey === kpiKey}
            onToggle={() => setOpenKey(openKey === kpiKey ? null : kpiKey)}
            rawValue={display.rawValue}
            prevValue={prev.rawValue}
            higherIsBetter={meta.higherIsBetter}
          />
        );
      })}
    </div>
  );

  const showPipelineSection =
    !isLoading &&
    funnelData &&
    funnelData.summary.total_leads > 0;

  if (allErrored) {
    return (
      <div className="space-y-4">
        <Eyebrow>Key metrics</Eyebrow>
        <ErrorState
          message="We couldn't load analytics for this period. Please try again."
          onRetry={() => {
            refetchBottleneck();
            refetchSummary();
            refetchFunnel();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DiagnosticSummary data={diagnosticData} isLoading={loadingDiagnostic} />
      <Eyebrow>Key metrics</Eyebrow>
      {kpiGrid}
      {showPipelineSection ? (
        <>
          <PipelineStats funnel={funnelData!} bottleneck={bottleneckData} />
          <MiniFunnelChart
            stages={funnelData!.stages}
            totalLeads={funnelData!.summary.total_leads}
          />
        </>
      ) : !isLoading && (
        <div className="rounded-lg border border-dashed border-border p-6 text-center space-y-1">
          <p className="text-sm font-medium text-muted-foreground">Pipeline Snapshot</p>
          <p className="text-xs text-muted-foreground">No leads in pipeline yet - add prospects or connect Meta Lead Ads to populate.</p>
        </div>
      )}
    </div>
  );
}
